import type { Address, DatabaseRecord, Enduser, FormFieldOptions, FormResponseValue } from "@tellescope/types-models";
import { labelForDatabaseRecord } from "../fields/DatabaseSelectField/label-for-database-record";

const cellValue = (record: DatabaseRecord, column: string) =>
  record.values.find(({ label }) => label === column)?.value;

const isAddressValue = (value: unknown): value is Address =>
  typeof value === "object" && value !== null && "state" in value && "zipCode" in value;

const matchesFilterAnswer = (cell: string, answerValue: unknown): boolean => {
  if (Array.isArray(answerValue)) {
    return answerValue.some((entry) => {
      if (typeof entry === "object" && entry !== null && "text" in entry) {
        const { text } = entry;
        return String(text) === cell;
      }
      return String(entry) === cell;
    });
  }

  if (typeof answerValue === "string" || typeof answerValue === "number") return String(answerValue) === cell;

  if (isAddressValue(answerValue)) {
    return cell === answerValue.city || cell === answerValue.state || cell === answerValue.zipCode;
  }

  if (typeof answerValue === "object" && answerValue !== null) {
    return Object.values(answerValue).some((nested) => matchesFilterAnswer(cell, nested));
  }

  return false;
};

const resolveEnduserState = (
  responses: FormResponseValue[],
  enduser: Pick<Enduser, "state"> | undefined
): string | undefined => {
  const answered = responses.find(
    (r) => r.answer.type === "Address" && r.intakeField === "Address" && isAddressValue(r.answer.value)
  )?.answer;

  if (answered?.type === "Address" && isAddressValue(answered.value) && answered.value.state) {
    return answered.value.state;
  }

  return enduser?.state || undefined;
};

export const filterDatabaseChoices = ({
  choices,
  options,
  responses,
  enduser,
}: {
  choices: DatabaseRecord[];
  options: FormFieldOptions | undefined;
  responses: FormResponseValue[];
  enduser?: Pick<Enduser, "state">;
}): DatabaseRecord[] => {
  const { databaseFilter, filterByEnduserState } = options ?? {};

  const crossFieldFiltered =
    databaseFilter?.fieldId && databaseFilter.databaseLabel
      ? choices.filter((record) => {
          const cell = cellValue(record, databaseFilter.databaseLabel ?? "");
          if (cell === undefined || cell === null || !String(cell).trim()) return true;

          const filterAnswer = responses.find((r) => r.fieldId === databaseFilter.fieldId)?.answer;
          if (!filterAnswer) return true;

          return matchesFilterAnswer(String(cell), filterAnswer.value);
        })
      : choices;

  const stateFiltered = (() => {
    if (!filterByEnduserState) return crossFieldFiltered;

    const state = resolveEnduserState(responses, enduser);
    if (!state) return crossFieldFiltered;

    return crossFieldFiltered.filter((record) => {
      const cell = record.values.find(({ label }) => label.trim().toLowerCase() === "state")?.value;
      if (cell === undefined || cell === null || String(cell) === "") return true;
      return String(cell) === state;
    });
  })();

  const seenLabels = new Set<string>();
  return stateFiltered.filter((record) => {
    const label = labelForDatabaseRecord(options ?? {}, record);
    if (seenLabels.has(label)) return false;
    seenLabels.add(label);
    return true;
  });
};
